import { Modal, Form, Input, Select } from '@bancobarigui/barihaus';
import { InputNumber, Upload, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { FormInstance } from '@bancobarigui/barihaus';
import type { UploadFile as _UploadFile } from 'antd/es/upload/interface';
import type { AmostraCardData } from '../types';
import * as styles from '../styles';
import { useState, useEffect } from 'react';
import { usePropertyTypeOptions } from '@/features/laudo/hooks/usePropertyTypeOptions';

interface AmostraModalProps {
  amostra: AmostraCardData;
  isOpen: boolean;
  form: FormInstance;
  onSave: () => void;
  onCancel: () => void;
  metodologia?: string | undefined;
  mode?: 'edit' | 'add';
}

export function AmostraModal({ amostra, isOpen, form, onSave, onCancel, metodologia, mode = 'edit' }: AmostraModalProps) {
  const isAvaliando = amostra.identificacao === 'Avaliando';
  const isTerreno = Form.useWatch('tipoImovel', form) === 'Terreno' || amostra.tipoImovel === 'Terreno';
  const isComparativoDireto = metodologia === 'Comparativo Direto de Mercado';
  const isAddMode = mode === 'add';
  const { options: propertyTypeOptions, loading: propertyTypeLoading } = usePropertyTypeOptions();
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Sincroniza preview com imagem existente quando modal abre
  useEffect(() => {
    if (isOpen && amostra.imagemUrl) {
      setImagePreview(amostra.imagemUrl);
    } else if (!isOpen) {
      setImagePreview(null);
    }
  }, [isOpen, amostra.imagemUrl]);

  const getModalTitle = () => {
    if (isAddMode) return 'Adicionar Nova Amostra';
    return `Editar ${isAvaliando ? 'Imóvel Avaliando' : `Amostra ${amostra.identificacao}`}`;
  };

  // Converte arquivo para base64
  const handleImageUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Apenas arquivos de imagem são permitidos!');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('A imagem deve ter menos de 5MB!');
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setImagePreview(base64);
      form.setFieldValue('imagemUrl', base64);
    };
    reader.readAsDataURL(file);

    return false; // Previne upload automático
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    form.setFieldValue('imagemUrl', null);
  };

  return (
    <Modal
      title={getModalTitle()}
      open={isOpen}
      onOk={onSave}
      onCancel={onCancel}
      width={600}
      okText={isAddMode ? 'Adicionar' : 'Salvar'}
      cancelText="Cancelar"
    >
      <Form form={form} layout="vertical" initialValues={amostra}>
        {/* Upload de Foto */}
        <Form.Item label="Foto do Imóvel" name="imagemUrl">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            {imagePreview ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9',
                  }}
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#ff4d4f',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DeleteOutlined style={{ fontSize: 12 }} />
                </button>
              </div>
            ) : (
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageUpload}
              >
                <div
                  style={{
                    width: 120,
                    height: 120,
                    border: '1px dashed #d9d9d9',
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#fafafa',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1890ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d9d9d9'; }}
                >
                  <PlusOutlined style={{ fontSize: 24, color: '#999' }} />
                  <span style={{ fontSize: 12, color: '#999', marginTop: 8 }}>Upload</span>
                </div>
              </Upload>
            )}
            <div style={{ fontSize: 12, color: '#8c8c8c', flex: 1 }}>
              <p style={{ margin: 0 }}>Formatos: JPG, PNG, WEBP</p>
              <p style={{ margin: '4px 0 0 0' }}>Tamanho máximo: 5MB</p>
            </div>
          </div>
        </Form.Item>

        <Form.Item
          label="Tipo de Imóvel"
          name="tipoImovel"
          rules={[{ required: true, message: 'Selecione o tipo de imóvel' }]}
        >
          <Select
            disabled={isAvaliando}
            loading={propertyTypeLoading}
            options={propertyTypeOptions}
          />
        </Form.Item>

        <Form.Item
          label="Endereço"
          name="endereco"
          rules={[{ required: isAddMode, message: 'Informe o endereço' }]}
        >
          <Input placeholder="Ex: Rua das Flores, 123" />
        </Form.Item>

        <div style={styles.formGridStyle}>
          <Form.Item
            label="Link do Anúncio"
            name="urlOriginal"
            rules={[{ required: isAddMode, message: 'Informe o link do anúncio' }]}
          >
            <Input placeholder="Ex: https://www.vivareal.com.br/imovel/..." />
          </Form.Item>

          <Form.Item label="Fonte do Anúncio" name="fonte">
            <Select
              allowClear
              placeholder="Selecione a fonte"
              options={[
                { value: 'vivareal', label: 'VivaReal', disabled: false },
                { value: 'zapimoveis', label: 'Zap Imóveis', disabled: false },
                { value: 'olx', label: 'OLX', disabled: false },
                { value: 'imovelweb', label: 'ImovelWeb', disabled: false },
                { value: 'outros', label: 'Outros', disabled: false },
              ]}
            />
          </Form.Item>
        </div>

        <div style={styles.formGridStyle}>
          <Form.Item
            label="Área Construída (m²)"
            name="areaPrivativa"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              decimalSeparator=","
              placeholder="0,00"
            />
          </Form.Item>

          <Form.Item
            label="Área do Terreno (m²)"
            name="areaTerreno"
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} decimalSeparator="," placeholder="0,00" />
          </Form.Item>
        </div>

        <div style={styles.formGridStyle}>
          <Form.Item
            label="Valor Total (R$)"
            name="valorTotal"
            rules={[
              { required: true, message: 'Informe o valor' },
              { type: 'number', min: 1, message: 'Valor deve ser maior que 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000}
              precision={2}
              decimalSeparator=","
              placeholder="R$ 0,00"
              formatter={(value) => value ? `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
              parser={(value) => value?.replace(/R\$\s?|(\.*)/g, '') as any}
            />
          </Form.Item>

          <Form.Item
            label="Idade Aparente (anos)"
            name="idadeAparente"
            rules={[{ required: !isTerreno, message: 'Informe a idade' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} max={200} step={1} precision={0} placeholder="0" />
          </Form.Item>
        </div>

        <Form.Item
          label="Estado de Conservação"
          name="estadoConservacao"
          rules={[{ required: !isTerreno, message: 'Selecione o estado' }]}
        >
          <Select
            options={[
              { value: 'Nova(até 5 anos)', label: 'Nova (até 5 anos)', disabled: false },
              { value: 'Nova|Regular', label: 'Nova | Regular', disabled: false },
              { value: 'Regular', label: 'Regular', disabled: false },
              { value: 'Regular|Reparos simples', label: 'Regular | Reparos simples', disabled: false },
              { value: 'Reparos simples', label: 'Reparos simples', disabled: false },
              { value: 'Reparos simples|Reparos importantes', label: 'Reparos simples | Reparos importantes', disabled: false },
              { value: 'Reparos importantes', label: 'Reparos importantes', disabled: false },
              { value: 'Reparos importantes|Sem valor', label: 'Reparos importantes | Sem valor', disabled: false },
              { value: 'Sem valor', label: 'Sem valor', disabled: false },
            ]}
          />
        </Form.Item>

        {!isTerreno && (
          <div style={{ ...styles.formGridStyle, gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <Form.Item
              label="Qtd. Quartos"
              name="quantidadeQuartos"
              rules={[{ required: isAddMode, message: 'Informe' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={20} step={1} precision={0} placeholder="0" />
            </Form.Item>

            <Form.Item
              label="Qtd. Banheiros"
              name="quantidadeBanheiros"
              rules={[{ required: isAddMode, message: 'Informe' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={20} step={1} precision={0} placeholder="0" />
            </Form.Item>

            <Form.Item
              label="Qtd. Vagas"
              name="quantidadeVagas"
              rules={[{ required: isAddMode, message: 'Informe' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={20} step={1} precision={0} placeholder="0" />
            </Form.Item>
          </div>
        )}

        {!isComparativoDireto && (
          <div style={styles.formGridStyle}>
            <Form.Item label="Padrão" name="padrao">
              <Select
                placeholder="Selecione"
                options={[
                  { label: 'Alto', value: 'Alto', disabled: false },
                  { label: 'Médio', value: 'Médio', disabled: false },
                  { label: 'Baixo', value: 'Baixo', disabled: false },
                  { label: 'Mínimo', value: 'Mínimo', disabled: false },
                ]}
              />
            </Form.Item>

            <Form.Item label="Topografia" name="topografia">
              <Select
                options={[
                  { value: 'Plano', label: 'Plano', disabled: false },
                  { value: 'Declive', label: 'Declive', disabled: false },
                  { value: 'Aclive', label: 'Aclive', disabled: false },
                ]}
              />
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
}
